// ProductGridCart.js
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { getImageUrl } from "../../../lib/helpers";
import useMermaTolerancia from '../../../lib/hooks/useMermaTolerancia';
import notFoundImage from "../../../assets/fox-not-found.webp";
import { CategoryPlaceholder } from "../../../lib/consts/categoryPlaceholders";
import { effectiveUnitPrice } from '../../../lib/helpers/effectivePrice';
import ProductCard from "../../Elements/ProductCard";

// Icono por tipo de ubicación (almacén principal / nevera / armario / sub-almacén)
const LOC_ICON = { principal: '🏠', nevera: '🧊', armario: '🗄️', sub_almacen: '📦' };
const locIcon = (kind) => LOC_ICON[kind] || '📍';
const fmtLocQty = (n) => Number(n || 0).toLocaleString('es-ES', { maximumFractionDigits: 3 });

const ProductGridCart = ({ products, loading, onAddToCart, costLine, discountRate = 0, discountLabel = null, stockByLoc = {}, bottomSpacerPx = 0 }) => {
  // Tolerancia de báscula del club (Configuración; default 0,10 g del sistema).
  const toleranciaG = useMermaTolerancia();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [draft, setDraft] = useState({});
  // Avisos de validación POR TARJETA (stock, merma, cantidad vacía). Antes eran
  // cuadros nativos del navegador: feos, bloqueantes y fuera del estilo de la
  // app (reporte del usuario, 17-ago). Se borran solos a los 4 s.
  const [notice, setNotice] = useState({});
  const noticeTimers = useRef({});
  // Sin esto, un aviso vivo al desmontar la rejilla (cambio de socio, cerrar el
  // POS) dispara un setState sobre un componente ya muerto (revisión 17-ago).
  useEffect(() => {
    const timers = noticeTimers.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, []);
  const warn = (productId, msg) => {
    setNotice((n) => ({ ...n, [productId]: msg }));
    clearTimeout(noticeTimers.current[productId]);
    noticeTimers.current[productId] = setTimeout(
      () => setNotice((n) => ({ ...n, [productId]: null })), 4000
    );
  };
  const [searchTerm, setSearchTerm] = useState("");

  const categories = useMemo(() => {
    const categorySet = new Set();
    categorySet.add("All");
    let hasUncategorizedLike = false;
    // Agregar categorías válidas (filtrar null, undefined y "Uncategorized"/"uncategorized")
    (products || []).forEach(p => {
      const categoryName = p.category?.name;
      if (categoryName && !["Uncategorized", "uncategorized"].includes(categoryName)) {
        categorySet.add(categoryName);
      } else if (!categoryName || ["Uncategorized", "uncategorized"].includes(categoryName)) {
        hasUncategorizedLike = true;
      }
    });
    // Agregar "Sin categoría" si hay productos sin categoría o con nombre "Uncategorized"
    if (hasUncategorizedLike) {
      categorySet.add("Sin categoría");
    }
    return Array.from(categorySet);
  }, [products]);

  // Si la categoría activa ya no existe en el surtido actual (p. ej. dispensario → productos), volver a Todas
  useEffect(() => {
    if (selectedCategory !== 'All' && !categories.includes(selectedCategory)) {
      setSelectedCategory('All');
    }
  }, [categories, selectedCategory]);

  // Filtrar por categoría y término de búsqueda
  const filtered = useMemo(() => {
    if (!products || products.length === 0) return [];
    return products.filter(p => {
      let matchesCategory = false;
      if (selectedCategory === "All") {
        matchesCategory = true;
      } else if (selectedCategory === "Sin categoría") {
        const n = p.category?.name;
        matchesCategory = !n || ["Uncategorized", "uncategorized", "Sin categoría"].includes(n);
      } else {
        matchesCategory = p.category?.name === selectedCategory;
      }
    // El buscador mira nombre Y categoría: el staff busca lo que lee en la
    // tarjeta, y la tarjeta enseña las dos cosas. Solo por nombre, un prerroll
    // "Apple Fritter" de la categoría "CBD Pre-Rolls" era invisible buscando
    // "CBD" — salían solo los botes con CBD en el nombre (reporte Kong House,
    // 20-ago-2026, con vídeo).
    const q = searchTerm.trim().toLowerCase();
    const matchesSearch = !q
      || p.name.toLowerCase().includes(q)
      || (p.category?.name || "").toLowerCase().includes(q);
    return matchesCategory && matchesSearch;
  });
  }, [products, selectedCategory, searchTerm]);

  if (loading) {
    return (
      <div className='wa-dispensario-products p-4'>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mb-4"></div>
            <p className="text-gray-500">Cargando productos...</p>
          </div>
        </div>
      </div>
    );
  }

  const updateDraft = (id, key, value) => {
    setDraft(d => ({ ...d, [id]: { ...d[id], [key]: value } }));
  };

  return (
    <div className='wa-dispensario-products min-w-0'>
      <div className="wa-dispensario-products-filters">
        {/* Tabs de categoría (Figma) */}
        <div className="mb-3 flex flex-wrap items-center gap-1 px-1">
          {categories.map(cat => {
            const label = cat === "All" ? "Todo" : cat;
            const active = selectedCategory === cat;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  active
                    ? "bg-[#F5F5F5] text-[#0A0A0A]"
                    : "text-[#737373] hover:text-[#0A0A0A] hover:bg-[#FAFAFA]"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Buscador de productos (Figma) */}
        {/* 320 de ancho, como el frame: a todo el ancho el campo pedía un
            término larguísimo que nadie escribe, y descolgaba la rejilla. */}
        <div className="mb-3 px-1">
          <div className="relative w-full sm:max-w-[320px]">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373] w-4 h-4 pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar producto"
              className="w-full h-9 rounded-md border border-[#E5E5E5] bg-white pl-9 pr-8 text-sm text-[#0A0A0A] placeholder:text-[#737373] focus:border-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)]"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#0A0A0A]"
                title="Limpiar búsqueda"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mensaje cuando no hay productos */}
      {(!products || products.length === 0) && (
        <div className="text-center py-12 text-gray-500">
          <p>No hay productos disponibles</p>
        </div>
      )}

      {/* Mensaje cuando no hay productos filtrados */}
      {products && products.length > 0 && filtered.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <p>
            {selectedCategory === "All"
              ? "No se encontraron productos"
              : `No se encontraron productos en la categoría "${selectedCategory}"`}
          </p>
        </div>
      )}

      {/* Products Grid - Tamaño fijo, sin estirar */}
      {filtered.length > 0 && (
      <div className="wa-dispensario-products-grid">
        {filtered.map(product => {
          const { id, name, unit_type, image_path } = product;
          const imageSrc = image_path ? getImageUrl(image_path) : null;
          // DISPONIBLE = físico − reservado en carritos abiertos (backend). El FE muestra y
          // opera sobre el disponible; cae al físico (stock_quantity) si el backend aún no
          // manda `available`. Ver docs/ARQUITECTURA_STOCK.md. Toda la lógica de abajo usa
          // esta variable, por eso se conserva el nombre `stock_quantity`.
          const stock_quantity = product.available != null ? Number(product.available) : Number(product.stock_quantity);
          // Precio según la línea de coste del socio (cae al normal si no aplica)
          const price = effectiveUnitPrice(product, costLine);
          // Peso por gramo con 3 decimales (como la báscula): step 0.001 para que
          // no se pierda precisión al teclear/guardar (3 cifras).
          // El armario del dispensario va a NEGATIVO por diseño: el backend lo permite
          // (`make_purchase` solo bloquea la sobreventa si el producto NO es de
          // dispensario) y se reconcilia con la recarga del encargado. La rejilla, en
          // cambio, bloqueaba igual en los dos modos, así que el navegador impedía
          // justo la venta que el servidor sí acepta. En dispensario se AVISA y se deja
          // pasar; en barra se bloquea, que es lo que hace el backend.
          const esDispensario = Boolean(product.is_dispensario);
          const step = unit_type === "weight" ? 0.001 : 1;
          const unitLabel = unit_type === "weight" ? "g" : "pcs";
          // En los avisos se dice "u", como en el carrito y en el resto de la app;
          // "pcs" es jerga interna de esta rejilla (revisión 17-ago).
          const unidad = unit_type === "weight" ? "g" : "u";
          const { qty = "", cost = "", bascula = "" } = draft[id] || {};

          // Descuento del socio aplicado al PRECIO efectivo. Por créditos (Euro): los
          // créditos son el importe FINAL que gasta el socio, así que los gramos se
          // calculan al precio CON descuento — mismo precio efectivo que la vía por peso.
          // (Bug previo: usaba el precio sin descuento → cantidad incorrecta.)
          const rate = Number(discountRate) || 0;
          const effPrice = parseFloat(price) * (1 - rate);
          const priceForCredits = effPrice > 0 ? effPrice : parseFloat(price);

          const displayedCost = unit_type === "weight"
            ? qty ? (parseFloat(qty) * parseFloat(price)).toFixed(2) : cost
            : "";
          const displayedQty = unit_type === "weight"
            ? cost ? (parseFloat(cost) / priceForCredits).toFixed(3) : qty
            : "";

          // Subtotal/total SIEMPRE desde los gramos canónicos (displayedQty), para que
          // coincida con lo que se añade al carrito tanto por peso como por créditos.
          const grams = parseFloat(displayedQty) || 0;
          const lineSubtotal = unit_type === "weight"
            ? grams * parseFloat(price)
            : (displayedCost ? parseFloat(displayedCost) : 0);
          const lineTotal = lineSubtotal * (1 - rate);
          const pct = Math.round(rate * 100);

          const categoriaLabel = (() => {
            const n = product.category?.name;
            if (!n) return 'Sin categoría';
            return ["Uncategorized", "uncategorized"].includes(n) ? 'Sin categoría' : n;
          })();

          // La imagen se pasa ya resuelta: el slot mantiene la relación de aspecto
          // dentro del contenedor que pone ProductCard. Sin foto → ilustración de la
          // categoría; el icono de «no encontrado» solo es red de seguridad si la
          // foto existe pero falla al cargar.
          const imagenNodo = (
            <div className="wa-dispensario-product-image-slot relative w-full">
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt={name}
                  loading="lazy"
                  decoding="async"
                  className="absolute left-0 top-0 h-full w-full object-cover"
                  onError={(e) => { e.target.src = notFoundImage; }}
                />
              ) : (
                <div className="absolute inset-0">
                  <CategoryPlaceholder category={product.category?.name} size="lg" />
                </div>
              )}
            </div>
          );

          // Lo que el frame de la tarjeta no contempla pero el mostrador usa: cuánto
          // queda, de qué ubicación sale y el aviso al pasarse. Va por el hueco
          // `children` de ProductCard, que existe justo para esto.
          const extrasDeLaTarjeta = (
            <>
              <p className={`text-[10px] ${stock_quantity > 0 ? 'text-[var(--muted-foreground)]' : 'font-semibold text-[var(--destructive)]'}`}>
                {stock_quantity > 0 ? `Quedan ${stock_quantity} u` : 'Sin stock'}
              </p>
              {(stockByLoc[String(id).toLowerCase()]?.length > 0) && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {stockByLoc[String(id).toLowerCase()].map((l) => {
                    const neg = Number(l.quantity) < 0;
                    return (
                      <span
                        key={l.location_id}
                        title={neg ? `${l.name} · en negativo, pendiente de reponer` : l.name}
                        className={`inline-flex items-center gap-0.5 rounded-xs px-1.5 py-0.5 text-[9px] font-medium ${
                          neg
                            ? 'bg-[var(--toast-error-bg)] text-[var(--destructive)] ring-1 ring-[var(--toast-error-border)]'
                            : 'bg-[var(--muted)] text-[var(--muted-foreground)]'
                        }`}
                      >
                        <span aria-hidden>{neg ? '⚠️' : locIcon(l.kind)}</span>
                        <span className="max-w-[70px] truncate">{l.name}</span>
                        <span className={`font-semibold ${neg ? 'text-[var(--destructive)]' : 'text-[var(--foreground)]'}`}>
                          {fmtLocQty(l.quantity)}u
                        </span>
                      </span>
                    );
                  })}
                </div>
              )}
              {notice[id] && (
                <p role="alert" className="mt-1 rounded-md border border-[var(--toast-warning-border)] bg-[var(--toast-warning-bg)] px-1.5 py-1 text-[10px] font-medium leading-tight text-[var(--toast-warning-text)]">
                  {notice[id]}
                </p>
              )}
            </>
          );

          // Productos de UNIDAD (la Barra): son exactamente la variante «Otros» del
          // Club_ProductCard, así que se usa el componente en vez de repintar la
          // tarjeta aquí. Los de peso siguen abajo: llevan báscula, merma y total de
          // línea, que el frame de la tarjeta no modela.
          if (unit_type === "fixed") {
            return (
              <ProductCard
                key={id}
                variant="otros"
                name={name}
                category={categoriaLabel}
                price={Number(price).toFixed(2)}
                unitLabel="u"
                stock={stock_quantity}
                image={imagenNodo}
                value={qty}
                onChange={(v) => updateDraft(id, 'qty', v)}
                onAdd={(e) => {
                  const pedido = Math.max(1, parseInt(qty, 10) || 1);
                  if (stock_quantity < pedido) {
                    warn(id, `Solo quedan ${stock_quantity} u disponibles.`);
                    if (!esDispensario) return;
                  }
                  onAddToCart({ ...product, quantity: pedido }, e);
                  updateDraft(id, 'qty', '');
                }}
                disabled={!esDispensario && stock_quantity < 1}
                addTestId={`add-to-cart-${id}`}
              >
                {extrasDeLaTarjeta}
              </ProductCard>
            );
          }

          return (
            <div
              key={id}
              /* La card por PESO (el dispensario). El frame distingue sus tres
                   estados con el trazo y solo con el trazo: ni sombra, ni relleno. */
                className="flex flex-col rounded-lg border border-[var(--border)] bg-[var(--card,#fff)] p-2 text-xs transition-colors hover:border-[var(--primary)]"
            >
              {/* Imagen con relación de aspecto fija. Sin foto → ilustración de la
                  categoría (`Club_CategoryPlaceholder`), que es lo que dice el
                  diseño; el icono genérico de «no encontrado» solo queda como
                  red de seguridad si la foto existe pero falla al cargar. */}
              <div className="wa-dispensario-product-image-slot relative w-full overflow-hidden rounded-md bg-[var(--muted)]">
                {imageSrc ? (
                  <img
                    src={imageSrc}
                    alt={name}
                    loading="lazy"
                    decoding="async"
                    className="absolute left-0 top-0 h-full w-full object-cover"
                    onError={(e) => { e.target.src = notFoundImage; }}
                  />
                ) : (
                  <div className="absolute inset-0">
                    <CategoryPlaceholder category={product.category?.name} size="lg" />
                  </div>
                )}
              </div>

              {/* Cabecera: categoría · nombre · precio. Es todo lo que lleva la
                  tarjeta en Figma (`Club_ProductCard`, Weed y Otros) — el stock
                  por ubicaciones es de la variante Almacén, no del TPV. Aquí se
                  conserva una línea discreta de stock porque quien cobra necesita
                  saber si queda género; el desglose por ubicación se ha quitado. */}
              <div className="mt-3 flex flex-col gap-2">
                <span className="inline-flex w-fit rounded-lg bg-[var(--muted)] px-2.5 py-0.5 text-[10px] font-medium text-[var(--muted-foreground)]">
                  {(() => {
                    const n = product.category?.name;
                    if (!n) return 'Sin categoría';
                    return ["Uncategorized", "uncategorized"].includes(n) ? 'Sin categoría' : n;
                  })()}
                </span>

                <div>
                  <h3 className="truncate text-sm font-medium leading-tight text-[var(--foreground)]">{name}</h3>
                  <p className="flex items-center gap-1 text-xs font-medium text-[var(--muted-foreground)]">
                    <span>{Number(price).toFixed(2)} €/{unit_type === "weight" ? "g" : "u"}</span>
                    {rate > 0 && (
                      <span className="inline-flex items-center rounded-xs bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700" title={discountLabel ? `Descuento del socio: ${discountLabel}` : 'Descuento del socio'}>
                        −{pct}% dto
                      </span>
                    )}
                  </p>
                  <p className={`text-[10px] ${stock_quantity > 0 ? 'text-[var(--muted-foreground)]' : 'font-semibold text-[var(--destructive)]'}`}>
                    {stock_quantity > 0
                      ? `Quedan ${stock_quantity}${unit_type === "weight" ? "g" : " u"}`
                      : 'Sin stock'}
                  </p>

                  {/* Desglose por ubicación. El rediseño de la card no lo contemplaba
                      —el frame de Figma es anterior a esta función— pero es dato que el
                      mostrador usa, así que se conserva y se pasa a tokens. */}
                  {(stockByLoc[String(id).toLowerCase()]?.length > 0) && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {stockByLoc[String(id).toLowerCase()].map((l) => {
                        const neg = Number(l.quantity) < 0;
                        return (
                          <span
                            key={l.location_id}
                            title={neg ? `${l.name} · en negativo, pendiente de reponer` : l.name}
                            className={`inline-flex items-center gap-0.5 rounded-xs px-1.5 py-0.5 text-[9px] font-medium ${
                              neg
                                ? 'bg-[var(--toast-error-bg)] text-[var(--destructive)] ring-1 ring-[var(--toast-error-border)]'
                                : 'bg-[var(--muted)] text-[var(--muted-foreground)]'
                            }`}
                          >
                            <span aria-hidden>{neg ? '⚠️' : locIcon(l.kind)}</span>
                            <span className="max-w-[70px] truncate">{l.name}</span>
                            <span className={`font-semibold ${neg ? 'text-[var(--destructive)]' : 'text-[var(--foreground)]'}`}>
                              {fmtLocQty(l.quantity)}{unit_type === "weight" ? "g" : "u"}
                            </span>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {notice[id] && (
                <p role="alert" className="mb-1 rounded-md border border-amber-200 bg-amber-50 px-1.5 py-1 text-[10px] font-medium leading-tight text-amber-800">
                  {notice[id]}
                </p>
              )}

              {(
                <>
                  {/* Peso y Euro en paralelo, con la etiqueta encima: es la
                      «Quantity Selector» del Club_ProductCard (dos Club_Input de
                      96 con gap 12). Iban apilados con la etiqueta al lado.
                      Báscula NO está en el diseño — es el peso real para calcular
                      merma — así que se queda debajo, a ancho completo. */}
                  <div className="mb-3 space-y-3">
                    <div className="flex gap-3">
                    <div className="flex flex-1 min-w-0 flex-col gap-1">
                      <label className="text-xs font-medium text-[var(--muted-foreground)]">Peso</label>
                      <input
                        type="number"
                        step={step}
                        min={step}
                        max={esDispensario ? undefined : stock_quantity}
                        value={displayedQty}
                        onChange={e => {
                          const value = e.target.value;
                          const numValue = parseFloat(value);
                          if (numValue > stock_quantity) {
                            warn(id, `Solo quedan ${stock_quantity}${unidad} disponibles.`);
                            if (!esDispensario) return;
                          }
                          updateDraft(id, 'qty', value);
                          updateDraft(id, 'cost', '');
                        }}
                        className="h-8 w-full min-w-0 rounded-sm border border-[var(--border)] bg-white px-2 text-sm text-[var(--foreground)] appearance-none focus:outline-none focus:ring-1 focus:ring-[var(--primary)]"
                        placeholder="0.0"
                      />
                    </div>
                    <div className="flex flex-1 min-w-0 flex-col gap-1">
                      <label className="text-xs font-medium text-[var(--muted-foreground)]">Euro</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={esDispensario ? undefined : stock_quantity * price}
                        value={displayedCost}
                        onChange={e => {
                          const value = e.target.value;
                          const numValue = parseFloat(value);
                          const maxCost = stock_quantity * price;
                          // El mismo criterio que el campo de Peso y que "+ Añadir": en
                          // dispensario se avisa y se deja pasar (el armario va a negativo por
                          // diseño), en barra se bloquea. Este campo se quedó fuera del arreglo
                          // del 18-ago, así que con el armario a 0 el tope era 0 € y NO se podía
                          // teclear ningún importe — mientras que en gramos sí. Cobrar por euros
                          // es justo el gesto del mostrador ("ponme 20 €"), y se quedó muerto.
                          if (numValue > maxCost) {
                            warn(id, `Con el stock que queda, el máximo son €${maxCost.toFixed(2)}.`);
                            if (!esDispensario) return;
                          }
                          updateDraft(id, 'cost', value);
                          updateDraft(id, 'qty', '');
                        }}
                        className="h-8 w-full min-w-0 rounded-sm border border-[var(--border)] bg-white px-2 text-sm text-[var(--foreground)] appearance-none focus:outline-none focus:ring-1 focus:ring-[var(--primary)]"
                        placeholder="0.0"
                      />
                    </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-[var(--muted-foreground)]" title="Peso real de la báscula (merma)">Báscula</label>
                      <input
                        type="number"
                        step="0.001"
                        min="0"
                        value={bascula}
                        onChange={e => updateDraft(id, 'bascula', e.target.value)}
                        className="h-8 w-full min-w-0 rounded-sm border border-[var(--border)] bg-white px-2 text-sm text-[var(--foreground)] appearance-none focus:outline-none focus:ring-1 focus:ring-[var(--primary)]"
                        placeholder="real (opcional)"
                      />
                    </div>
                  </div>
                  {/* Total de la línea ya calculado (con descuento del socio si tiene) */}
                  {(displayedQty || displayedCost) && (
                    <div className="mb-1 flex items-center justify-between rounded-md bg-[var(--muted)] px-2 py-1 text-[11px]">
                      <span className="text-[#737373]">{rate > 0 ? `Total (−${pct}%)` : 'Total'}</span>
                      <span className="font-semibold text-[#0A0A0A]">
                        {rate > 0 && (
                          <span className="mr-1 text-[10px] font-normal text-[#A3A3A3] line-through">€{lineSubtotal.toFixed(2)}</span>
                        )}
                        €{lineTotal.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <button
                    data-testid={`add-to-cart-${id}`}
                    onClick={(e) => {
                      const finalQty = parseFloat(displayedQty);
                      if (!finalQty || finalQty <= 0) {
                        warn(id, 'Pon una cantidad antes de añadir.');
                        return;
                      }
                      const realW = parseFloat(bascula);
                      // Guardarraíl de merma: si se pesa en báscula, la merma (real − cobrado)
                      // no puede superar la tolerancia del club (Configuración; default
                      // ±0,10 g) → bloquea y avisa. El backend re-valida al cobrar.
                      const MERMA_MAX = toleranciaG;
                      if (!isNaN(realW) && realW > 0) {
                        const merma = realW - finalQty;
                        if (Math.abs(merma) > MERMA_MAX) {
                          warn(id, 
                            `Merma fuera de rango: ${merma > 0 ? '+' : ''}${merma.toFixed(3)}${unitLabel}. ` +
                            `No puede superar ±${MERMA_MAX.toFixed(2)} g. Revisa el peso de la báscula.`
                          );
                          return;
                        }
                      }
                      // Validar stock contra lo que se descuenta (báscula si existe, si no lo cobrado)
                      const deduct = (!isNaN(realW) && realW > 0) ? realW : finalQty;
                      if (deduct > stock_quantity) {
                        warn(id, `Solo quedan ${stock_quantity}${unidad} disponibles.`);
                        if (!esDispensario) return;
                      }
                      onAddToCart({ ...product, quantity: finalQty, realWeight: (!isNaN(realW) && realW > 0) ? realW : undefined }, e);
                      setDraft(d => ({ ...d, [id]: { qty: "", cost: "", bascula: "" } }));
                    }}
                    disabled={!esDispensario && stock_quantity <= 0}
                    // Este es el botón de los productos POR PESO: se colorea solo
                    // cuando hay gramos/euros tecleados, porque sin cantidad pulsar
                    // no añade nada y un botón primario que no hace nada miente.
                    // El de unidades (ProductCard) sí va primario siempre — reporte
                    // Kong House 20-ago.
                    className={`mt-auto inline-flex w-full touch-manipulation items-center justify-center gap-1 rounded-sm py-1.5 text-xs font-semibold transition-colors ${
                      stock_quantity <= 0
                        ? 'cursor-not-allowed bg-[var(--muted)] text-[#A3A3A3]'
                        : (displayedQty || displayedCost)
                          ? 'bg-[var(--primary)] text-[var(--primary-foreground)] hover:bg-[var(--primary-strong)] active:scale-95'
                          : 'border border-[var(--border)] bg-white text-[var(--foreground)] hover:bg-[var(--muted)]'
                    }`}
                    title={stock_quantity <= 0 ? 'Sin stock disponible' : 'Agregar al carrito'}
                  >
                    {stock_quantity <= 0 ? 'Sin stock' : '+ Añadir'}
                  </button>
                </>
              )}
            </div>
          );
        })}
        {bottomSpacerPx > 0 && (
          <div
            aria-hidden
            className="pointer-events-none"
            style={{ gridColumn: '1 / -1', height: bottomSpacerPx }}
          />
        )}
      </div>
      )}
    </div>
  );
};

export default ProductGridCart;
